export type AvifConversionProgress = {
  /** Overall 0–100 across the whole batch */
  overall: number;
  /** Current file index (1-based) */
  currentIndex: number;
  total: number;
  fileName: string;
  stage: 'converting' | 'done' | 'error';
  message?: string;
};

const MAX_SIDE = 4096;

/** cqLevel: 0 (best) – 63 (smallest). ~28 keeps quality high with smaller files. */
const AVIF_QUALITY = 28;

type AvifEncode = (
  data: ImageData,
  options?: Record<string, unknown>
) => Promise<ArrayBuffer>;

let encodeFn: AvifEncode | null = null;
let encoderReady: Promise<AvifEncode> | null = null;

const getEncoder = (): Promise<AvifEncode> => {
  if (encodeFn) return Promise.resolve(encodeFn);
  if (!encoderReady) {
    encoderReady = (async () => {
      const encodeMod = await import('@jsquash/avif/encode');
      const encode = encodeMod.default as AvifEncode;
      const init = encodeMod.init as ((moduleOrPath?: unknown) => Promise<unknown>) | undefined;

      if (typeof init === 'function') {
        try {
          const wasm = await import('@jsquash/avif/codec/enc/avif_enc.wasm?url');
          await init(wasm.default);
        } catch {
          // Some builds self-locate the wasm; init without an explicit URL.
          await init();
        }
      }

      encodeFn = encode;
      return encode;
    })();
  }
  return encoderReady;
};

const fileToImageData = async (file: File): Promise<ImageData> => {
  const bitmap = await createImageBitmap(file);
  try {
    let { width, height } = bitmap;
    const longest = Math.max(width, height);
    if (longest > MAX_SIDE) {
      const scale = MAX_SIDE / longest;
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));
    }

    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not create canvas context for AVIF conversion');
    ctx.drawImage(bitmap, 0, 0, width, height);
    return ctx.getImageData(0, 0, width, height);
  } finally {
    bitmap.close();
  }
};

/**
 * Convert one image to AVIF in the browser (WASM), ready for Supabase Storage upload.
 */
export const convertOneImageToAvif = async (
  file: File,
  onFileProgress?: (value: number) => void
): Promise<File> => {
  if (file.type === 'image/avif' || file.name.toLowerCase().endsWith('.avif')) {
    onFileProgress?.(100);
    return file;
  }

  onFileProgress?.(10);
  const encode = await getEncoder();
  onFileProgress?.(25);

  const imageData = await fileToImageData(file);
  onFileProgress?.(55);

  const avifBuffer = await encode(imageData, {
    cqLevel: AVIF_QUALITY,
    cqAlphaLevel: -1,
    subsample: 1,
    speed: 6,
  });
  onFileProgress?.(90);

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
  const out = new File([avifBuffer], `${baseName}.avif`, {
    type: 'image/avif',
    lastModified: Date.now(),
  });
  onFileProgress?.(100);
  return out;
};

/**
 * Convert many images to AVIF client-side, then upload them to Supabase Storage as usual.
 */
export const convertImagesToAvif = async (
  files: File[],
  onProgress?: (progress: AvifConversionProgress) => void
): Promise<File[]> => {
  if (!files.length) return [];

  const converted: File[] = [];

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const baseOverall = (i / files.length) * 100;

    onProgress?.({
      overall: Math.round(baseOverall),
      currentIndex: i + 1,
      total: files.length,
      fileName: file.name,
      stage: 'converting',
      message: `Conversion AVIF ${i + 1}/${files.length} — ${file.name}`,
    });

    try {
      const avif = await convertOneImageToAvif(file, (fileProgress) => {
        onProgress?.({
          overall: Math.min(99, Math.round(baseOverall + fileProgress / files.length)),
          currentIndex: i + 1,
          total: files.length,
          fileName: file.name,
          stage: 'converting',
          message: `Conversion AVIF ${i + 1}/${files.length} — ${file.name}`,
        });
      });
      converted.push(avif);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Conversion failed';
      onProgress?.({
        overall: Math.round(baseOverall),
        currentIndex: i + 1,
        total: files.length,
        fileName: file.name,
        stage: 'error',
        message,
      });
      throw error;
    }
  }

  onProgress?.({
    overall: 100,
    currentIndex: files.length,
    total: files.length,
    fileName: converted[converted.length - 1]?.name || '',
    stage: 'done',
    message: 'Conversion terminée',
  });

  return converted;
};
